import { formatDateToMMDDYYYY } from './dateUtils';

export const REPORT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type ReportPageSizeOption = (typeof REPORT_PAGE_SIZE_OPTIONS)[number];

/** Placeholder shown in a report cell that has no value. */
export const REPORT_EMPTY_VALUE = '—';

export function formatReportEmptyValue(value: unknown): string {
  return value === null || value === undefined || value === ''
    ? REPORT_EMPTY_VALUE
    : String(value);
}

export const displayDash = formatReportEmptyValue;

/**
 * Format a date-only value (`YYYY-MM-DD`) for a report cell, falling back to
 * the shared empty placeholder. Parses in local time, so the rendered day
 * never shifts.
 */
export function formatReportDate(value: string | null | undefined): string {
  return value ? formatDateToMMDDYYYY(value) : REPORT_EMPTY_VALUE;
}

export function formatEnumReason(
  value: string | null | undefined,
  fallback = 'unavailable'
): string {
  return value ? value.replace(/_/g, ' ') : fallback;
}

export function readPositiveIntegerParam(
  value: string | null,
  fallback = 1
): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export function readChoiceParam<T extends string | number>(
  value: string | null,
  validValues: readonly T[],
  fallback: T
): T {
  if (value === null) return fallback;
  const match = validValues.find(option => String(option) === value);
  return match ?? fallback;
}

export function readOptionalChoiceParam<T extends string | number>(
  value: string | null,
  validValues: readonly T[]
): T | undefined {
  if (value === null) return undefined;
  return validValues.find(option => String(option) === value);
}

export function readBooleanParam(value: string | null): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

export function coerceReportPageSize(value: number): ReportPageSizeOption {
  return readChoiceParam(String(value), REPORT_PAGE_SIZE_OPTIONS, 25);
}

export function readDelimitedParam(value: string, delimiter = ','): string[] {
  return value
    .split(delimiter)
    .map(part => part.trim())
    .filter(Boolean);
}
